import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CardGiftcardRoundedIcon from '@mui/icons-material/CardGiftcardRounded';
import useCheckoutSuccess from './useCheckoutSuccess';
import styles from './StripeReturn.module.css';

const formatMonthlyPrice = (priceCents) => new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL'
}).format((priceCents || 0) / 100);

const isOnboarding = (setupStatus) =>
  !!setupStatus && (setupStatus.status === 'INITIAL' || setupStatus.status === 'IN_PROGRESS');

/** Volta do checkout pago: recibo do que foi contratado e atalhos para seguir. */
const Success = () => {
  const navigate = useNavigate();
  const { sessionData, isLoading, error, setupStatus } = useCheckoutSuccess();

  const handleContinue = () => {
    // Onboarding incompleto vai para o assistente; senão, para o início
    navigate(isOnboarding(setupStatus) ? '/setup-wizard' : '/dashboard');
  };

  if (isLoading) {
    return (
      <div className={`page-content ${styles.page}`}>
        <div className={styles.card}>
          <Skeleton variant="circular" width={56} height={56} sx={{ mx: 'auto' }} />
          <Skeleton variant="text" width="60%" height={36} sx={{ mx: 'auto' }} />
          <Skeleton variant="rounded" height={140} sx={{ borderRadius: '14px' }} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`page-content ${styles.page}`}>
        <div className={styles.card}>
          <span className={`${styles.icon} ${styles.iconError}`}><ErrorOutlineIcon fontSize="inherit" /></span>
          <h1 className={styles.title}>Não foi possível confirmar agora</h1>
          <p className={styles.text}>{error}</p>
          <div className={styles.actions}>
            <button type="button" className={styles.btnPrimary} onClick={() => { window.location.href = '/login'; }}>
              Ir para o login
            </button>
          </div>
        </div>
      </div>
    );
  }

  const showSummary = sessionData && sessionData.has_active_plan && sessionData.plan_name;

  return (
    <div className={`page-content ${styles.page}`}>
      <div className={styles.card}>
        <span className={styles.icon}><CheckRoundedIcon fontSize="inherit" /></span>
        <h1 className={styles.title}>Assinatura confirmada</h1>
        <p className={styles.text}>Tudo pronto. Os recursos do seu plano já estão liberados.</p>

        {showSummary && (
          <dl className={styles.receipt}>
            <div className={styles.receiptRow}>
              <dt>Plano</dt>
              <dd><span className={styles.planPill}>{sessionData.plan_name}</span></dd>
            </div>
            {sessionData.price_cents > 0 && (
              <div className={styles.receiptRow}>
                <dt>Valor por mês</dt>
                <dd className={styles.amount}>{formatMonthlyPrice(sessionData.price_cents)}</dd>
              </div>
            )}
            {sessionData.next_billing_date && (
              <div className={styles.receiptRow}>
                <dt>Próxima cobrança</dt>
                <dd>{sessionData.next_billing_date}</dd>
              </div>
            )}
            {sessionData.trial_ends_at && (
              <div className={styles.receiptRow}>
                <dt>Teste gratuito até</dt>
                <dd>{new Date(sessionData.trial_ends_at).toLocaleDateString('pt-BR')}</dd>
              </div>
            )}
          </dl>
        )}

        <p className={styles.note}>Você pode gerenciar a assinatura quando quiser em Meu plano.</p>

        <button type="button" className={styles.referral} onClick={() => navigate('/indique')}>
          <CardGiftcardRoundedIcon className={styles.referralIcon} />
          <span>
            <strong>Indique e ganhe.</strong> Quando alguém que você indicou assina, você ganha um mês do seu plano.
          </span>
        </button>

        <div className={styles.actions}>
          <button type="button" className={styles.btnSecondary} onClick={() => navigate('/subscription/details')}>
            Ver meu plano
          </button>
          <button type="button" className={styles.btnPrimary} onClick={handleContinue}>
            {isOnboarding(setupStatus) ? 'Continuar a configuração' : 'Ir para o início'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Success;
